import { readProjectSettings } from './adminSettings';
import { readAdminSession } from './authProvider';

export type DiagnosisCategory = 'MissingVoice' | 'ReadingDropdown' | 'ReadingDragDrop' | 'MockTestStructure' | 'DescribeImagePreprocessing' | 'SummarizeGroupDiscussionPreprocessing' | 'RetellLecturePreprocessing' | 'SummarizeWrittenTextPreprocessing' | 'RespondToSituationPreprocessing' | 'WriteEssayPreprocessing' | 'WriteEmailPreprocessing' | 'SummarizeSpokenTextPreprocessing';

export type DiagnosisIssue = {
    code: string;
    category: DiagnosisCategory;
    message: string;
    examTaskId?: string | null;
    questionNo?: string | null;
    displayTitle?: string | null;
    examTaskType?: string | null;
    examName?: string | null;
    mockTestId?: string | null;
    mockTestTitle?: string | null;
};

export type DiagnosisResult = {
    validatedAt: string;
    scannedQuestionCount: number;
    scannedMockTestCount: number;
    issues: DiagnosisIssue[];
};

type AdminApiEnvelope<T> = {
    success?: boolean;
    message?: string;
    data?: T;
    result?: T;
};

function getAdminApiConfig() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const baseUrl = String(
        import.meta.env.VITE_API_BASE_URL ??
            import.meta.env.VITE_API_URL ??
            settings.adminApiBaseUrl ??
            'https://test-api.pteultra.com/api/v1',
    ).replace(/\/+$/g, '');
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const adminToken = String(
        session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '',
    );
    return { baseUrl, apiVersion, adminToken };
}

function trimSlashes(value: string): string {
    return value.replace(/^\/+|\/+$/g, '');
}

function hasVersionedApiPath(value: string): boolean {
    return /\/api\/v\d+(?:\.\d+)?\/?$/i.test(value);
}

function getErrorMessage(payload: unknown, fallback: string): string {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload && typeof payload === 'object') {
        const record = payload as Record<string, unknown>;
        for (const key of ['message', 'error', 'title']) {
            if (typeof record[key] === 'string' && record[key].trim()) return record[key];
        }
    }
    return fallback;
}

function unwrap<T>(payload: T | AdminApiEnvelope<T>): T {
    if (payload && typeof payload === 'object') {
        const envelope = payload as AdminApiEnvelope<T>;
        if (envelope.success === false) throw new Error(envelope.message || 'Validation failed.');
        if (envelope.data !== undefined) return envelope.data;
        if (envelope.result !== undefined) return envelope.result;
    }
    return payload as T;
}

async function validate(path: string): Promise<DiagnosisResult> {
    const { baseUrl, apiVersion, adminToken } = getAdminApiConfig();
    const apiRoot = hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    const url = new URL(`${apiRoot}/AdminQuestionDiagnosis/${trimSlashes(path)}`);
    const headers = new Headers({ Accept: 'application/json' });
    if (adminToken) headers.set('Authorization', `Bearer ${adminToken}`);

    const response = await fetch(url.toString(), { method: 'POST', headers });
    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('application/json') ? await response.json() : await response.text();
    if (!response.ok) throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    return unwrap(payload as DiagnosisResult | AdminApiEnvelope<DiagnosisResult>);
}

export function validateQuestions(): Promise<DiagnosisResult> {
    return validate('validate-questions');
}

export function validateMockTests(): Promise<DiagnosisResult> {
    return validate('validate-mock-tests');
}

export function validateAiSourcePreprocessing(): Promise<DiagnosisResult> {
    return validate('validate-ai-source-preprocessing');
}
